// Phase 5B / Iter95 — One-Click Refresh Both action.
//
// Sits in the Shared Cockpit Safety Strip and orchestrates exactly
// two sequential builds the operator can already trigger
// manually from the snapshot tile + the WES context builder tile:
//
//   1. POST /api/forge/toolbox/market-snapshot/build
//   2. POST /api/forge/toolbox/wes-context/build
//
// The results are then published into the existing in-memory
// `MarketSnapshotContext` + `WESContextProvider`, which makes the
// rest of the cockpit (strip chips, auto-scan panels' WES
// provenance strips, payload builders) light up automatically.
//
// HARD CONSTRAINTS (asserted by Iter95 tests):
//  * Manual-only. No effect hooks. No mount-time HTTP. No
//    scheduler. No polling. No silent retry. No background
//    refresh.
//  * Exactly two outbound POST call sites, targeting only the
//    two existing read-only endpoints above. No new endpoint
//    introduced.
//  * No scanner preview endpoint. No Market Scanner control
//    plane. No Wheel candidates / WES-GEX. No broker / account /
//    position / order / journal / admin path.
//  * No persistence layer (no localStorage / sessionStorage /
//    IndexedDB / document.cookie).
//  * No ranking, no winner output, no auto-select language
//    outside disclaimer copy + their testids.
//  * If snapshot succeeds and WES fails (or vice versa), the
//    overall outcome is `partial` — the operator sees both
//    states separately. No silent retry.

import React, { useState } from "react";
import axios from "axios";
import {
  RefreshCw, ShieldCheck, AlertTriangle, Check, X, Loader,
} from "lucide-react";
import { API } from "@/App";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import { useWESContext } from "@/contexts/WESContextProvider";

const SNAPSHOT_ENDPOINT = "/forge/toolbox/market-snapshot/build";
const WES_ENDPOINT = "/forge/toolbox/wes-context/build";

const DEFAULT_SYMBOLS = "SPY";

const SAFETY_LABELS = Object.freeze([
  { testid: "refresh-both-label-refresh-only",
    text: "Refreshes context only." },
  { testid: "refresh-both-label-no-scan",
    text: "No scanner run." },
  { testid: "refresh-both-label-no-ranking",
    text: "No ranking." },
  { testid: "refresh-both-label-no-execution",
    text: "No execution." },
  { testid: "refresh-both-label-human-decides",
    text: "Human decides." },
]);

function parseSymbols(text) {
  return Array.from(new Set(
    (text || "")
      .split(/[\s,;\n]+/)
      .map(s => s.trim().toUpperCase())
      .filter(Boolean)
  ));
}

function resolveDefaultSymbols(snapshotRequest, wesRequest) {
  const fromSnap = Array.isArray(snapshotRequest?.symbols)
    ? snapshotRequest.symbols : null;
  const fromWes = Array.isArray(wesRequest?.symbols)
    ? wesRequest.symbols : null;
  const pick = fromSnap && fromSnap.length > 0
    ? fromSnap
    : (fromWes && fromWes.length > 0 ? fromWes : null);
  return pick ? pick.join(", ") : DEFAULT_SYMBOLS;
}

function extractErrorMessage(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  }
  return e?.message || "Network error";
}

export default function RefreshBothControl() {
  const {
    setSnapshot: publishSnapshot,
    request: snapshotRequest,
  } = useMarketSnapshot();
  const {
    setWESContext: publishWesContext,
    request: wesRequest,
  } = useWESContext();

  const [symbolsText, setSymbolsText] = useState(
    resolveDefaultSymbols(snapshotRequest, wesRequest)
  );
  // Status machine:
  //   "idle" → "refreshing-snapshot" → "refreshing-wes" → terminal
  //   terminal ∈ { "complete", "partial", "error" }
  const [status, setStatus] = useState("idle");
  const [snapshotResult, setSnapshotResult] = useState(null); // "ok" | "fail"
  const [wesResult, setWesResult] = useState(null);
  const [snapshotErr, setSnapshotErr] = useState(null);
  const [wesErr, setWesErr] = useState(null);

  async function refreshBoth() {
    const symbols = parseSymbols(symbolsText);
    if (symbols.length === 0) {
      setSnapshotErr("Enter at least one symbol.");
      setSnapshotResult("fail");
      setWesErr("Skipped — no symbols provided.");
      setWesResult("fail");
      setStatus("error");
      return;
    }
    if (symbols.length > 25) {
      setSnapshotErr(`Hard cap is 25 symbols (got ${symbols.length}).`);
      setSnapshotResult("fail");
      setWesErr("Skipped — cap exceeded.");
      setWesResult("fail");
      setStatus("error");
      return;
    }
    setSnapshotResult(null);
    setWesResult(null);
    setSnapshotErr(null);
    setWesErr(null);

    // ─── Step 1: snapshot ────────────────────────────────────────
    setStatus("refreshing-snapshot");
    const snapPayload = {
      symbols,
      expiration_policy: "auto_dte_window",
      include_ivr: true,
      max_symbols: 25,
    };
    let snapOk = false;
    try {
      const r = await axios.post(`${API}${SNAPSHOT_ENDPOINT}`, snapPayload);
      publishSnapshot(r.data, snapPayload);
      setSnapshotResult("ok");
      snapOk = true;
    } catch (e) {
      setSnapshotErr(extractErrorMessage(e));
      setSnapshotResult("fail");
    }

    // ─── Step 2: WES context ─────────────────────────────────────
    setStatus("refreshing-wes");
    const wesPayload = {
      symbols,
      primary: "1H",
      context: "D",
      max_symbols: 25,
      require_closed_bar: true,
    };
    let wesOk = false;
    try {
      const r = await axios.post(`${API}${WES_ENDPOINT}`, wesPayload);
      publishWesContext(r.data, wesPayload);
      setWesResult("ok");
      wesOk = true;
    } catch (e) {
      setWesErr(extractErrorMessage(e));
      setWesResult("fail");
    }

    // ─── Terminal status ─────────────────────────────────────────
    if (snapOk && wesOk) setStatus("complete");
    else if (snapOk || wesOk) setStatus("partial");
    else setStatus("error");
  }

  const refreshing = status === "refreshing-snapshot"
    || status === "refreshing-wes";

  return (
    <div
      data-testid="refresh-both-control"
      className="border-t border-[#26262B] px-4 py-2 bg-[#08080A]"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <RefreshCw size={12} className="text-[#E0E0E0]" />
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#E0E0E0] font-bold">
          Refresh Both
        </span>
        <label className="flex items-center gap-1">
          <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
            symbols
          </span>
          <input
            type="text"
            data-testid="refresh-both-symbols"
            value={symbolsText}
            onChange={e => setSymbolsText(e.target.value)}
            spellCheck={false}
            className="bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-0.5 text-[10px] font-mono text-[#E0E0E0] focus:outline-none w-56"
          />
        </label>
        <button
          type="button"
          data-testid="refresh-both-run"
          onClick={refreshBoth}
          disabled={refreshing}
          className="px-3 py-1 rounded-sm border border-[#E0E0E0]/30 bg-[#E0E0E0]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#E0E0E0] hover:bg-[#E0E0E0]/20 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {refreshing ? <Loader size={10} className="animate-spin" />
                      : <RefreshCw size={10} />}
          {status === "refreshing-snapshot" && "Refreshing snapshot…"}
          {status === "refreshing-wes" && "Refreshing WES…"}
          {!refreshing && "Refresh Snapshot + WES"}
        </button>

        <StatusChip status={status} />

        <StepChip
          testid="refresh-both-snapshot-status"
          label="SNAPSHOT"
          state={status === "refreshing-snapshot"
            ? "running"
            : (snapshotResult || "idle")}
        />
        <StepChip
          testid="refresh-both-wes-status"
          label="WES"
          state={status === "refreshing-wes"
            ? "running"
            : (wesResult || "idle")}
        />
      </div>

      <div className="flex items-center gap-3 flex-wrap mt-1 text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]">
        <ShieldCheck size={10} />
        {SAFETY_LABELS.map(l => (
          <span key={l.testid} data-testid={l.testid}>
            {l.text}
          </span>
        ))}
      </div>

      {snapshotErr && (
        <div
          data-testid="refresh-both-snapshot-error"
          className="mt-1 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1 rounded-sm"
        >
          <AlertTriangle size={11} className="shrink-0 mt-0.5" />
          <span>
            <span className="font-bold uppercase tracking-[0.12em]">
              Snapshot refresh failed —
            </span>{" "}
            {snapshotErr}
          </span>
        </div>
      )}
      {wesErr && (
        <div
          data-testid="refresh-both-wes-error"
          className="mt-1 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1 rounded-sm"
        >
          <AlertTriangle size={11} className="shrink-0 mt-0.5" />
          <span>
            <span className="font-bold uppercase tracking-[0.12em]">
              WES refresh failed —
            </span>{" "}
            {wesErr}
          </span>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  if (status === "idle") return null;
  const map = {
    "refreshing-snapshot": {
      label: "REFRESHING SNAPSHOT",
      cls: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
      icon: <Loader size={10} className="animate-spin" />,
      tid: "refresh-both-status-refreshing-snapshot",
    },
    "refreshing-wes": {
      label: "REFRESHING WES",
      cls: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
      icon: <Loader size={10} className="animate-spin" />,
      tid: "refresh-both-status-refreshing-wes",
    },
    "complete": {
      label: "COMPLETE",
      cls: "border-[#00FF88]/40 bg-[#00FF88]/5 text-[#00FF88]",
      icon: <Check size={10} />,
      tid: "refresh-both-status-complete",
    },
    "partial": {
      label: "PARTIAL",
      cls: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
      icon: <AlertTriangle size={10} />,
      tid: "refresh-both-status-partial",
    },
    "error": {
      label: "ERROR",
      cls: "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]",
      icon: <X size={10} />,
      tid: "refresh-both-status-error",
    },
  };
  const e = map[status];
  if (!e) return null;
  return (
    <span
      data-testid={e.tid}
      className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1 ${e.cls}`}
    >
      {e.icon}{e.label}
    </span>
  );
}

function StepChip({ testid, label, state }) {
  const map = {
    idle:    { txt: "—",       cls: "border-[#26262B] text-[#5F5F66]" },
    running: { txt: "running", cls: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]" },
    ok:      { txt: "ok",      cls: "border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88]" },
    fail:    { txt: "failed",  cls: "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]" },
  };
  const e = map[state] || map.idle;
  return (
    <span
      data-testid={testid}
      className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${e.cls}`}
    >
      {label}: {e.txt}
    </span>
  );
}
